/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/**
 * Renders a stack of overlapping, slightly-rotated product logo icons for
 * Rovo Space sources.
 *
 * Accepts the raw `SpaceSourceResponse[]` from the Spaces API and dynamically
 * resolves the provider icon for each source URL via the SmartLink client
 * (same pattern as `SmartLinkSourceRow` in `CreateSpaceModal`).
 *
 * The rotation angles mirror those used by `AppIconWrapper` in
 * `conversation-assistant-content-renderer`'s `SourceButtonIcons`:
 *   index 1 → -6°, index 3 → 8°
 *
 * Requires a `<SmartCardProvider>` ancestor.
 */
import React from 'react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { ProviderIconView } from '@atlassian/conversation-assistant-utils/ProviderIconView';
import { useSmartLinkMetadata } from '@atlassian/conversation-assistant-utils/useSmartLinkMetadata';

export type SpaceSourceResponse = {
	/** The unique identifier for the source (UUID). Use this as the sourceId for delete operations. */
	id: string;
	ari: string;
	url: string;
	sourceType: string;
	name?: string | null;
};

const MAX_VISIBLE_LOGOS = 3;

/** Mirrors the rotation angles in SourceButtonIcons/AppIconWrapper. */
const ICON_ROTATIONS: Record<number, number> = {
	1: -6,
	3: 8,
};

const styles = cssMap({
	outerContainer: {
		display: 'inline-flex',
		alignItems: 'center',
	},
	overflowCount: {
		marginInlineEnd: token('space.050'),
	},
	container: {
		display: 'inline-flex',
		alignItems: 'center',
		position: 'relative',
	},
	logoWrapper: {
		position: 'relative',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
		marginInlineStart: token('space.negative.100'),
	},
	firstLogo: {
		marginInlineStart: token('space.negative.025'),
	},
});

/**
 * Resolves and renders a single rotated provider icon for a source URL.
 * Falls back to nothing while the SmartLink is pending or on error.
 */
const RotatedSourceIcon = ({
	url,
	index,
	total,
}: {
	url: string;
	index: number;
	total: number;
}) => {
	const { providerIcon } = useSmartLinkMetadata(url);
	const rotation = ICON_ROTATIONS[index];

	return (
		<Box
			xcss={cx(styles.logoWrapper, index === 0 && styles.firstLogo)}
			style={{
				zIndex: total - index,
				transform: rotation ? `rotate(${rotation}deg)` : undefined,
			}}
		>
			<ProviderIconView icon={providerIcon} />
		</Box>
	);
};

interface RotatedSourceLogoStackProps {
	sources: SpaceSourceResponse[];
}

/**
 * Renders overlapping, slightly-rotated provider icons for Rovo Space sources,
 * resolved dynamically via the SmartLink client.
 * Deduplicates by URL and shows up to 3 icons with a "+X" overflow count.
 *
 * Requires a `<SmartCardProvider>` ancestor.
 */
export const RotatedSourceLogoStack = ({
	sources,
}: RotatedSourceLogoStackProps): React.JSX.Element | null => {
	if (sources.length === 0) {
		return null;
	}

	// Deduplicate by URL so each source gets its own SmartLink resolution.
	const allUniqueUrls = Array.from(new Set(sources.map((s) => s.url)));
	const visibleUrls = allUniqueUrls.slice(0, MAX_VISIBLE_LOGOS);
	const overflowCount = allUniqueUrls.length - visibleUrls.length;

	return (
		<Box xcss={styles.outerContainer}>
			<Box xcss={styles.container}>
				{visibleUrls.map((url, index) => (
					<RotatedSourceIcon key={url} url={url} index={index} total={visibleUrls.length} />
				))}
			</Box>
			{overflowCount > 0 && (
				<Box xcss={styles.overflowCount}>
					<Text size="small" color="color.text.subtlest">
						+{overflowCount}
					</Text>
				</Box>
			)}
		</Box>
	);
};
